import { useTranslation } from "react-i18next";
import { Pause } from "lucide-react";
import { ConfirmDialog } from "@tellescope/ui";
import { formatDate } from "@tellescope/utils";
import { getStripePlanProductName } from "../../../hooks/useStripeSubscriptions";
import type { StripeSubscription } from "../../../hooks/useStripeSubscriptions";
import { useStripeSubscriptionMutation } from "../../../hooks/useStripeSubscriptionMutations";

interface PauseConfirmDialogProps {
  subscription: StripeSubscription;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function PauseConfirmDialog({ subscription, open, onOpenChange, onSuccess }: PauseConfirmDialogProps) {
  const { t } = useTranslation("payments");
  const pauseMutation = useStripeSubscriptionMutation();

  const { effectivePlan } = subscription;
  const productName = getStripePlanProductName(effectivePlan);
  const currentPeriodEnd = formatDate(subscription.current_period_end * 1000);

  const handleConfirm = () => {
    pauseMutation.mutate(
      {
        integration: "Stripe",
        type: "subscription-pause",
        id: subscription.id,
        query: { behavior: "mark_uncollectible" },
      },
      { onSuccess }
    );
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      variant="warning"
      icon={<Pause className="h-5 w-5" />}
      title={t("Pause Subscription")}
      description={
        productName
          ? t(
              "Pausing your subscription to {{productName}} will temporarily stop billing. You can resume at any time.",
              { productName }
            )
          : t("Pausing your subscription will temporarily stop billing. You can resume at any time.")
      }
      confirmText={t("Pause Subscription")}
      cancelText={t("Cancel")}
      onConfirm={handleConfirm}
      loading={pauseMutation.isPending}
      customContent={
        <div className="mt-2 p-3 bg-muted rounded-md text-sm">
          <p className="text-muted-foreground">
            {t("Your access will continue until {{date}}, then billing will be paused until you choose to resume.", {
              date: currentPeriodEnd,
            })}
          </p>
        </div>
      }
    />
  );
}
